import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/common/Button";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import useCrud from "../../components/common/crud/useCrud";
import lotService from "../../services/lotService";
import stockCategoryService from "../../services/stockCategoryService";
import stockProcessingService from "../../services/stockProcessingService";
import alertService from "../../utils/alertService";
import { formatDate } from "../../utils/dateUtils";

function StockTransferListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [lotOptions, setLotOptions] = useState([]);
  const [stockCategoryOptions, setStockCategoryOptions] = useState([]);

  const endpoints = useMemo(() => ({ list: "/stock-processing/transfer" }), []);
  const {
    rows,
    isLoading,
    error,
    filters,
    pagination,
    sortConfig,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "transferMasterId",
    initialFilters: {
      search: searchParams.get("search") || "",
      fromLotId: searchParams.get("fromLotId") || "",
      stockCategoryId: searchParams.get("stockCategoryId") || "",
    },
    initialSort: { key: "createdDate", order: "DESC" },
  });

  const handleCreateTransfer = () => {
    navigate("/transfer-stock/create");
  };

  const handleRevert = async (item) => {
    const result = await alertService.confirm(
      `Are you sure you want to revert this transfer of ${Number(
        item.transferWeight || 0,
      ).toLocaleString()} Kg? This will return the stock to lot ${
        item.fromLotNo || ""
      } and category ${item.fromStockCategoryName || ""}.`,
      "Revert Stock Transfer",
    );

    if (result.isConfirmed) {
      try {
        await stockProcessingService.revertStockTransfer(item.transferMasterId);
        alertService.success("Transfer reverted successfully");
        refresh();
      } catch (err) {
        alertService.error(err.message || "Failed to revert transfer");
      }
    }
  };

  const handleClearFilters = () => {
    setSearchParams(new URLSearchParams());
    updateFilters({
      search: "",
      fromLotId: "",
      stockCategoryId: "",
    });
  };

  useEffect(() => {
    let isMounted = true;
    const fetchFilters = async () => {
      try {
        const [lotsRes, categoriesRes] = await Promise.all([
          lotService.getActiveLots({ showLocked: true }),
          stockCategoryService.getActiveStockCategories(),
        ]);
        if (!isMounted) return;

        const lots = lotsRes?.items || (Array.isArray(lotsRes) ? lotsRes : []);
        const lotOpts = [
          { label: "All Lots", value: "" },
          ...lots.map((lot) => ({
            label: lot.lotNo,
            value: String(lot.lotId),
          })),
        ];
        setLotOptions(lotOpts);

        const categories =
          categoriesRes?.items ||
          (Array.isArray(categoriesRes) ? categoriesRes : []);
        setStockCategoryOptions([
          { label: "All Categories", value: "" },
          ...categories.map((c) => ({
            label: c.stockCategoryName,
            value: String(c.stockCategoryId),
          })),
        ]);
      } catch (err) {
        console.error("Failed to fetch filters", err);
      }
    };
    fetchFilters();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("page", String(filters.page || 1));
    next.set("limit", String(filters.limit || 10));
    next.set("sort", filters.sort || "createdDate");
    next.set("order", filters.order || "DESC");
    if (filters.search) next.set("search", filters.search);
    if (filters.fromLotId) next.set("fromLotId", String(filters.fromLotId));
    if (filters.stockCategoryId)
      next.set("stockCategoryId", String(filters.stockCategoryId));
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  const tableColumns = useMemo(
    () => [
      {
        key: "createdDate",
        label: "Date",
        sortable: true,
        render: (item) => formatDate(item.createdDate),
      },
      {
        key: "fromLotNo",
        label: "From Lot",
        sortable: true,
        render: (item) => item.fromLotNo || "-",
      },
      {
        key: "fromStockCategoryName",
        label: "From Category",
        sortable: true,
        render: (item) => item.fromStockCategoryName || "-",
      },
      {
        key: "toLotNo",
        label: "To Lot",
        sortable: true,
        render: (item) => item.toLotNo || "-",
      },
      {
        key: "toStockCategoryName",
        label: "To Category",
        sortable: true,
        render: (item) => item.toStockCategoryName || "-",
      },
      {
        key: "transferWeight",
        label: "Weight (Kg)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right font-mono font-bold text-primary-600",
        render: (item) => Number(item.transferWeight || 0).toLocaleString(),
      },
      {
        key: "actions",
        label: "Actions",
        className: "text-right w-20",
        render: (item) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-50 rounded-full"
              onClick={() => handleRevert(item)}
              title="Revert Transfer"
            >
              <i className="material-icons text-lg">history</i>
            </Button>
          </div>
        ),
      },
    ],
    [refresh],
  );

  return (
    <div className="space-y-3">
      {/* <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            Stock <span className="text-primary-600">Transfer</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            View and manage history of stock transfers between lots
          </p>
        </div>
        
      </div> */}

      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={tableColumns}
        rows={rows}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={updateFilters}
        pagination={pagination}
        sortConfig={sortConfig}
        onSort={changeSort}
        onPageChange={changePage}
        searchPlaceholder="Search Transfers..."
        tableScrollHeight="55vh"
        showActions={false}
        toolbarFilters={
          <div className="flex items-center gap-2 min-w-[280px] w-full">
            <SearchableDropdown
              value={String(filters.fromLotId || "")}
              options={lotOptions}
              placeholder="Filter by Lot"
              onChange={(e) => updateFilters({ fromLotId: e.target.value })}
            />
            <Button variant="primary" size="sm" className="ml-auto" onClick={handleCreateTransfer}>
              <i className="material-icons text-base mr-1">add</i> New Transfer
            </Button>
          </div>
        }
      />
    </div>
  );
}

export default StockTransferListPage;
